import Course from "../interfaces/course.interface";
import CourseStaff from "../interfaces/course-staff.interface";
import {
  PaginateInstitutions,
  PaginateCourseStaff,
  PaginateCourseByUser,
} from "../interfaces/pagination.interface";
import http from "./axios";
import {
  addFilesToBody,
  convertObjectToFormData,
  downloadFile,
  handleError,
} from "./http-utils.service";
import { UploadFile } from "antd";
import { createObjCourse } from "../components/course/components/utils";
import {
  RegisterCourseStatusResponse,
  RegisterCourses,
  RegisterCoursesResponse,
} from "../interfaces/register-courses.interface";
import { CourseGradeData } from "../interfaces/course-grade.interface";
import {
  RegistrationDetailsInterface,
  RegistrationSnapshot,
} from "../interfaces/registration.interface";
import { StudentCourseStatus } from "../interfaces/students.interface";

type CourseResponse = {
  success: boolean;
  data: {
    course: Course;
  };
};

export const deleteCourseMember = async (courseStaffId: number) => {
  const response = await http.delete<{ success: boolean; message: string }>(
    `/courses-staff/${courseStaffId}`,
  );
  return response;
};

export const getCourseMembers = async (props: PaginateCourseStaff) => {
  const { courseId, limit, skip, query } = props;
  const options = {
    params: {
      limit,
      skip,
      ...(query ? { search: query } : {}),
    },
  };
  try {
    const response = await http.get<{ count: number; list: CourseStaff[] }>(
      `/courses-staff/courses/${courseId}`,
      options,
    );
    const count = response.data.count;
    const list = response.data.list.map((item) => {
      if (item.role === "GE") {
        item.role = "General Enquiries";
      } else if (item.role === "CO") {
        item.role = "Coordinator";
      } else if (item.role === "IN") {
        item.role = "Instructor";
      } else if (item.role === "TA") {
        item.role = "Teaching Assistant";
      }
      return item;
    });
    return { list, count };
  } catch (e) {
    throw handleError(e);
  }
};

export const getCoursesByInstitution = async (props: PaginateInstitutions) => {
  const { institutionId, limit, skip, query, type, orderBy, sort } = props;
  const options = {
    params: {
      limit,
      skip,
      ...(query ? { search: query } : {}),
      type: type === "All" ? {} : type,
      ...(orderBy && { orderBy }),
      ...(sort && { sort }),
    },
  };
  try {
    const response = await http.get<{ count: number; list: Course[] }>(
      `/institutions/${institutionId}/courses`,
      options,
    );
    return response.data;
  } catch (e) {
    throw handleError(e);
  }
};

export const getCoursesByStudent = async (
  props: PaginateCourseByUser,
  extraFilter?: Record<string, string>,
) => {
  const { userId, institutionId, limit, skip, query, type, orderBy, sort } =
    props;
  const options: { params: Record<string, string | number | unknown> } = {
    params: {
      ...(userId && { userId }),
      limit,
      skip,
      ...(institutionId && { institutionId }),
      ...(query ? { search: query } : {}),
      type: type === "All" ? {} : type,
      ...(orderBy && { orderBy }),
      ...(sort && { sort }),
    },
  };
  if (extraFilter) {
    for (const key in extraFilter) {
      const value = extraFilter[key];
      if (Array.isArray(value)) {
        options.params[key] = value.join(",");
      } else {
        options.params[key] = value;
      }
    }
  }

  try {
    const response = await http.get<{ count: number; list: Course[] }>(
      `/institutions/${institutionId}/student-courses`,
      options,
    );
    return response.data;
  } catch (e) {
    throw handleError(e);
  }
};

export const getCoursesByStaff = async (props: PaginateCourseByUser) => {
  const { institutionId, limit, skip, query, type, orderBy, sort } = props;
  const options = {
    params: {
      limit,
      skip,
      ...(query ? { search: query } : {}),
      type: type === "All" ? {} : type,
      ...(orderBy && { orderBy }),
      ...(sort && { sort }),
    },
  };
  try {
    const response = await http.get<{ count: number; list: Course[] }>(
      `/institutions/${institutionId}/staff-courses`,
      options,
    );
    return response.data;
  } catch (e) {
    throw handleError(e);
  }
};

export const getCourseRegistrationByUser = async (
  courseId: string,
  userId: number,
) => {
  try {
    courseId = courseId.includes("clone") ? courseId.split("-")[0] : courseId;
    const response = await http.get<RegisterCourseStatusResponse>(
      `courses/${courseId}/users/${userId}/registration`,
    );
    return response.data;
  } catch (e) {
    throw handleError(e);
  }
};

export const getCourseById = async (id: string = "") => {
  try {
    id = id.includes("clone") ? id.split("-")[0] : id;
    if (!id.includes("New")) {
      const response = await http.get<Course>(`/courses/${id}`);
      return response.data;
    } else {
      return createObjCourse(id);
    }
  } catch (e) {
    throw handleError(e);
  }
};

export const getStudentCourseStatus = async ({
  userId,
  courseId,
}: {
  userId: number;
  courseId: number;
}) => {
  try {
    const response = await http.get<StudentCourseStatus>(
      `/students-courses/record`,
      {
        params: { userId, courseId },
      },
    );
    return response.data;
  } catch (e) {
    throw handleError(e);
  }
};

export const getUserCourseRole = async ({
  userId,
  courseId,
}: {
  userId: number;
  courseId: number;
}) => {
  try {
    const response = await http.get<CourseStaff>(`/courses/${courseId}/role`, {
      params: { userId },
    });
    if (response.data.role) {
      return response.data;
    }
    return null;
  } catch (e) {
    throw handleError(e);
  }
};

export const getRegisterByCourse = async (id: string = "") => {
  try {
    const response = await http.get<any>(`/courses/${id}/options`);
    return response.data.map((c: any, i: number) => ({
      ...c,
      key: `cost-${i}`,
    }));
  } catch (e) {
    throw handleError(e);
  }
};

export const cloneCourse = async (id: number) => {
  try {
    const response = await http.post<Course>(`/courses/${id}/clone/`);
    return response.data;
  } catch (e) {
    throw handleError(e);
  }
};

export const registerCourse = async (register: RegisterCourses) => {
  try {
    const response = await http.post<RegisterCoursesResponse>(
      `/courses/register/`,
      register,
    );
    return response.data;
  } catch (e) {
    throw handleError(e);
  }
};

export const createCourse = async (course: Course) => {
  try {
    const formData = convertObjectToFormData(course);
    addFilesToBody(formData, course.filesList);
    const response = await http.post<CourseResponse>(`/courses/`, formData, {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    });
    return response.data;
  } catch (e) {
    throw handleError(e);
  }
};

export const updateCourseById = async (
  id: string = "",
  course: Course,
  files: UploadFile[],
) => {
  try {
    id = id.includes("clone") ? id.split("-")[0] : id;
    const formData = convertObjectToFormData(course);
    addFilesToBody(formData, files);
    const response = await http.put<CourseResponse>(
      `/courses/${id}`,
      formData,
      { headers: { "Content-Type": "application/x-www-form-urlencoded" } },
    );
    return response.data;
  } catch (e) {
    throw handleError(e);
  }
};

export const downloadFileById = async (id: string = "", name: string) => {
  try {
    const response = await http.get<{ url: string }>(
      `/attached-files/${id}/file`,
      {
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
        },
      },
    );
    const res = await http.get<Blob>(response.data.url, {
      responseType: "blob",
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
      },
    });
    downloadFile(res.data, name);

    return res.data;
  } catch (e) {
    throw handleError(e);
  }
};

export const getCourseGradeAssignments = async (id: string) => {
  try {
    const response = await http.get<CourseGradeData>(
      `courses/${id}/grades-details`,
    );
    return response.data;
  } catch (err) {
    throw handleError(err);
  }
};

export const getRegistrationSnapshot = async (id: string) => {
  try {
    const response = await http.get<RegistrationSnapshot[]>(
      `courses/${id}/registration-snapshot`,
    );
    return response.data;
  } catch (err) {
    throw handleError(err);
  }
};

export const getRegistrationDetails = async (id: string) => {
  try {
    const response = await http.get<RegistrationDetailsInterface[]>(
      `courses/${id}/registration-details`,
    );
    return response.data;
  } catch (err) {
    throw handleError(err);
  }
};
